// src/components/Common/ColumnSelectorPopover.jsx
import React from 'react';
import {
  Popover,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Button,
  Divider,
} from '@mui/material';
import { ViewColumn as ViewColumnIcon, RestartAlt as ResetIcon } from '@mui/icons-material';

/**
 * ColumnSelectorPopover
 * 
 * @param {boolean} open
 * @param {HTMLElement} anchorEl
 * @param {Function} onClose
 * @param {Array<{ key: string, label: string, required?: boolean }>} columns
 * @param {Array<string>} visibleColumns - List of keys currently visible
 * @param {Function} onToggle - (columnKey) => void
 * @param {Function} onReset - () => void
 * @param {boolean} isDark
 */
export default function ColumnSelectorPopover({
  open,
  anchorEl,
  onClose,
  columns = [],
  visibleColumns = [],
  onToggle,
  onReset,
  isDark = true,
}) {
  const isAllSelected = columns.every(col => visibleColumns.includes(col.key));

  const handleSelectAllToggle = () => {
    if (isAllSelected) {
      // Keep only required columns
      const requiredKeys = columns.filter(c => c.required).map(c => c.key);
      requiredKeys.forEach(k => {
        if (!visibleColumns.includes(k)) onToggle(k);
      });
      columns.filter(c => !c.required).forEach(c => {
        if (visibleColumns.includes(c.key)) onToggle(c.key);
      });
    } else {
      // Select all
      columns.forEach(c => {
        if (!visibleColumns.includes(c.key)) onToggle(c.key);
      });
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        elevation: 8,
        sx: {
          width: 250,
          p: 1.5,
          borderRadius: '8px',
          bgcolor: isDark ? '#1E1E1E' : '#FFFFFF',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'}`,
          boxShadow: isDark ? '0 10px 30px rgba(0,0,0,0.6)' : '0 10px 30px rgba(0,0,0,0.1)',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <ViewColumnIcon sx={{ fontSize: 16, color: '#4F46E5' }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem', color: isDark ? '#F1F5F9' : '#0F172A' }}>
            Table Columns
          </Typography>
        </Box>
        {onReset && (
          <Button
            size="small"
            startIcon={<ResetIcon sx={{ fontSize: 13 }} />}
            onClick={onReset}
            sx={{
              fontSize: '0.68rem',
              textTransform: 'none',
              py: 0,
              px: 0.75,
              minWidth: 'auto',
              color: isDark ? '#94A3B8' : '#64748B',
              '&:hover': { color: '#4F46E5' },
            }}
          >
            Reset
          </Button>
        )}
      </Box>

      <Divider sx={{ my: 0.75, borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }} />

      <Box sx={{ maxHeight: 280, overflowY: 'auto', pr: 0.5 }}>
        <FormGroup>
          {columns.map(col => {
            const isChecked = visibleColumns.includes(col.key);
            const isRequired = Boolean(col.required);

            return (
              <FormControlLabel
                key={col.key}
                control={
                  <Checkbox
                    size="small"
                    checked={isChecked}
                    disabled={isRequired}
                    onChange={() => onToggle(col.key)}
                    sx={{
                      p: '3px 6px',
                      color: isDark ? '#64748B' : '#94A3B8',
                      '&.Mui-checked': { color: '#4F46E5' },
                      '&.Mui-disabled': { color: isDark ? '#475569' : '#CBD5E1' },
                    }}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.74rem',
                      fontWeight: isChecked ? 600 : 400,
                      color: isRequired
                        ? (isDark ? '#64748B' : '#94A3B8')
                        : (isDark ? '#E2E8F0' : '#334155'),
                    }}
                  >
                    {col.label} {isRequired && '(Always visible)'}
                  </Typography>
                }
                sx={{ m: 0, py: '1px' }}
              />
            );
          })}
        </FormGroup>
      </Box>

      <Divider sx={{ my: 0.75, borderColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }} />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 0.25 }}>
        <Button
          size="small"
          onClick={handleSelectAllToggle}
          sx={{
            fontSize: '0.7rem',
            textTransform: 'none',
            fontWeight: 600,
            py: 0.25,
            px: 0.75,
            color: '#4F46E5',
          }}
        >
          {isAllSelected ? 'Deselect Optional' : 'Select All'}
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={onClose}
          sx={{
            fontSize: '0.7rem',
            textTransform: 'none',
            fontWeight: 600,
            py: 0.25,
            px: 1.25,
            bgcolor: '#4F46E5',
            '&:hover': { bgcolor: '#4338CA' },
            borderRadius: '4px',
            boxShadow: 'none',
          }}
        >
          Done
        </Button>
      </Box>
    </Popover>
  );
}
